// Finance Config normalizer.
//
// The native shape (all new clients, and the new base) declares modules:
//   {
//     "engagementType": "coaching" | "programme"   (older rows: coachingBlock / retainerCoaching, mapped on read)
//     "billing": { "region": "nz" | "international", "invoicing": "xero" | "hub" },   // optional; derived from currency when absent
//     "engagementStart": "2026-02",
//     "currency": "NZD",            // optional; "USD" for Keynes
//     "tax": "gst",                 // optional; "none" when GST-exempt
//     "ledgerLookAhead": 3,         // optional; future months shown as projections
//     "timeZone": "America/Chicago",// optional; the client's HQ zone (IANA). Timed
//     "timeZoneLabel": "Austin",    //   milestones then show there and in NZ (src/time.js)
//     "roadmapSessions": true,      // optional; the roadmap's "Coaching sessions" switch starts on
//     "modules": {
//       "retainer":          { "label", "amount", "includes": [], "monthlyTotal",
//                              "invoices": [{ number, date, month, amount, status, lines: [{ label, amount }] }] },
//       "recurringSessions": { "sessions": [{ id, label, type?, coach?, status?, from?, to? }], "rolloverValue",
//                              "mode"?: "sessions" | "seats", "seats"?: [{ type, label, count }] },
//                            // status: active (default) | paused | optedOut | ended — only active rows
//                            // appear in a month; from/to ("YYYY-MM") bound a person's months.
//                            // Additions may carry started ("YYYY-MM", the label's month when
//                            // billing began later than the start) and detail ("extra session
//                            // per month") — display only.
//                            // mode "seats" (Keynes): coaching is included in the retainer as seat
//                            // counts; the ledger shows the counts and only the month's joiners /
//                            // leavers (from/to), never per-person Happened rows.
//       "drawdownPool":      { "label"?, "monthlyAccrual", "openingBalance"? },
//       "rateCard":          { "rates": [{ id, label, rate, unit? }] },   // unit defaults to "/hr"
//     }
//   }
//   Finance Data months[m].lineItems[] may carry `billedSeparately: true` —
//   one-off work invoiced on completion (e.g. a Fresh Eyes Pulse phase) that
//   is listed in the month but never drawn from the pool.
//   {
//     "modules": {
//       "sessionBlock":      { ... }   // built with the Niru onboarding
//     }
//   }
//
// The LEGACY flat shape (36 Degrees' live record) is adapted here at load
// time, non-destructively — the stored record is never rewritten. Legacy
// gating is preserved exactly: the old UI showed the finance tab only when
// retainer > 0, and then always rendered all four sections, so a legacy
// config declares all four modules or none.

import { getCurrentMonth } from "./calc";
import { regionOf, invoicingOf, typeKey } from "./presets";

export default function normalizeConfig(raw) {
  if (!raw || typeof raw !== "object") {
    return { engagementType: null, engagementStart: getCurrentMonth(), ledgerLookAhead: 0, currency: "NZD", tax: "gst", billing: { region: "nz", invoicing: "xero" }, modules: {} };
  }

  if (raw.modules && typeof raw.modules === "object") {
    return {
      engagementType: raw.engagementType ? typeKey(raw.engagementType) : null,
      engagementStart: raw.engagementStart || getCurrentMonth(),
      ledgerLookAhead: Number(raw.ledgerLookAhead) || 0,
      currency: raw.currency || "NZD",
      tax: raw.tax || "gst",
      billing: { region: regionOf(raw), invoicing: invoicingOf(raw) },
      ...(raw.timeZone ? { timeZone: raw.timeZone, timeZoneLabel: raw.timeZoneLabel || null } : {}),
      roadmapSessions: raw.roadmapSessions === true, // coaching sessions on the roadmap by default (src/client/sessions.js)
      modules: raw.modules,
    };
  }

  // Legacy flat shape. Defaults mirror the old FALLBACK_CONFIG merge.
  const modules = {};
  if ((raw.retainer || 0) > 0) {
    const fixedBlock = raw.fixedBlock || { amount: 0, label: "Leadership programme" };
    modules.retainer = {
      label: fixedBlock.label,
      amount: fixedBlock.amount,
      includes: raw.fixedComponents || [],
      monthlyTotal: raw.retainer,
    };
    modules.drawdownPool = { monthlyAccrual: raw.drawdown || 0 };
    modules.recurringSessions = {
      sessions: raw.coachingSessions || [],
      rolloverValue: raw.coachingRollover || 0,
    };
    modules.rateCard = { rates: raw.rateCard || [] };
  }

  return {
    roadmapSessions: raw.roadmapSessions === true,
    engagementType: raw.engagementType ? typeKey(raw.engagementType) : null,
    engagementStart: raw.engagementStart || getCurrentMonth(),
    ledgerLookAhead: 0,
    currency: "NZD",
    tax: "gst",
    billing: { region: "nz", invoicing: "xero" },
    modules,
  };
}
